import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Bell, CheckCircle2, RefreshCw, AlertTriangle, Info, Zap } from "lucide-react";
import PanelState, { extractApiError } from "@/components/PanelState";

// ============================================================================
// Platform Alerts admin tile (2026-07-31).
// Mock-Slack sink for the Dispatch Assist Very-High + permit escalations.
// Real Slack lands in a later phase — until then this page IS the alert surface.
// ============================================================================

const SEVERITY_TONE = {
  critical: { bg: "#fff0f0", fg: "#7a1c1c", border: "#f5c2c2", icon: AlertTriangle, label: "Critical" },
  warn:     { bg: "#fff4d6", fg: "#8a5b00", border: "#f3d97a", icon: AlertTriangle, label: "Warn" },
  info:     { bg: "#e7f4fb", fg: "#1c6a99", border: "#c5e0ee", icon: Info,          label: "Info" },
};

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], {
      month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AdminPlatformAlerts() {
  const [items, setItems] = useState([]);
  const [load, setLoad] = useState({ status: "loading", error: null });
  const [showAcked, setShowAcked] = useState(false);
  const [busyRowId, setBusyRowId] = useState(null);

  const load_ = useCallback(async () => {
    setLoad({ status: "loading", error: null });
    try {
      const params = { limit: 100 };
      if (!showAcked) params.acknowledged = false;
      const { data } = await api.get("/admin/platform-alerts", { params });
      setItems(data?.items || []);
      setLoad({ status: "ok", error: null });
    } catch (err) {
      setItems([]);
      setLoad({ status: "error", error: extractApiError(err) });
    }
  }, [showAcked]);

  useEffect(() => { load_(); }, [load_]);

  const ackOne = async (id) => {
    setBusyRowId(id);
    try {
      await api.patch(`/admin/platform-alerts/${id}/ack`);
      await load_();
    } catch (err) {
      window.alert(extractApiError(err)?.message || "Ack failed");
    } finally {
      setBusyRowId(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-platform-alerts-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[.15em] text-[color:var(--cc-ink-muted)] font-semibold">
            Platform admin
          </div>
          <h1 className="font-display font-bold text-[26px] sm:text-[32px] tracking-tight text-[#0e1420] mt-1 inline-flex items-center gap-2.5">
            <Bell size={22} strokeWidth={1.6} className="text-[#0072bc]" />
            Platform Alerts
          </h1>
          <p className="text-[13.5px] text-[color:var(--cc-ink-muted)] mt-1 max-w-2xl">
            Mock-Slack sink. Any Very-High-tier or permit-flagged Dispatch Assist evaluation lands here until real Slack integration ships in a later phase.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-[12.5px] cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="alerts-toggle-acked"
              checked={showAcked}
              onChange={(e) => setShowAcked(e.target.checked)}
              className="accent-[#0072bc]"
            />
            Show acknowledged
          </label>
          <button
            onClick={load_}
            data-testid="alerts-refresh"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-2 border border-[#e7e9ee] rounded-lg hover:bg-[#f5f6f8]"
          >
            <RefreshCw size={14} strokeWidth={1.8} /> Refresh
          </button>
        </div>
      </header>

      {load.status !== "ok" || items.length === 0 ? (
        <PanelState
          status={load.status} error={load.error}
          isEmpty={items.length === 0}
          emptyTitle={showAcked ? "No alerts on record" : "Inbox zero"}
          emptyBody={showAcked
            ? "No Dispatch Assist alerts have ever been logged."
            : "No unacknowledged alerts. Very-High or permit-flagged evaluations will land here."}
          testIdPrefix="platform-alerts"
        />
      ) : (
        <div className="space-y-3">
          {items.map((a) => {
            const tone = SEVERITY_TONE[a.severity] || SEVERITY_TONE.info;
            const Icon = tone.icon;
            return (
              <div
                key={a.id}
                data-testid={`alert-row-${a.id}`}
                className={`rounded-xl border p-4 bg-white shadow-sm ${a.acknowledged ? "opacity-60" : ""}`}
                style={{ borderColor: tone.border }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider border"
                        style={{ background: tone.bg, color: tone.fg, borderColor: tone.border }}
                      >
                        <Icon size={11} strokeWidth={2} /> {tone.label}
                      </span>
                      <span className="text-[11.5px] font-mono-data text-[color:var(--cc-ink-muted)]">
                        {a.event_type}
                      </span>
                      <span className="text-[11.5px] text-[color:var(--cc-ink-muted)]">·</span>
                      <span className="text-[11.5px] text-[color:var(--cc-ink-muted)]">{fmt(a.created_at)}</span>
                      {a.acknowledged && (
                        <span className="inline-flex items-center gap-1 text-[11.5px] text-[#0e8a68] font-semibold ml-1">
                          <CheckCircle2 size={11} strokeWidth={2} /> acknowledged
                        </span>
                      )}
                    </div>
                    <div className="font-display font-semibold text-[15px] text-[#0e1420] mt-1.5">
                      {a.title}
                    </div>
                    <pre className="text-[12.5px] text-[#0e1420] mt-2 whitespace-pre-wrap font-body leading-relaxed">
                      {a.body}
                    </pre>
                    <div className="mt-2 flex items-center gap-3 text-[11.5px] text-[color:var(--cc-ink-muted)] flex-wrap">
                      <span>Targets: <b>{(a.target_recipients || []).join(", ") || "—"}</b></span>
                      <span>·</span>
                      <span>CSR: {a.created_by || "—"}</span>
                      {a.tenant_id && (<><span>·</span><span>Tenant: <span className="font-mono-data">{a.tenant_id}</span></span></>)}
                    </div>
                  </div>
                  {!a.acknowledged && (
                    <button
                      disabled={busyRowId === a.id}
                      onClick={() => ackOne(a.id)}
                      data-testid={`alert-ack-${a.id}`}
                      className="shrink-0 inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-[#0072bc] text-white hover:bg-[#005a95] disabled:opacity-50"
                    >
                      <CheckCircle2 size={13} strokeWidth={2} /> Acknowledge
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] flex items-center gap-2 pt-2">
        <Zap size={12} strokeWidth={1.8} />
        Mocked delivery — no real Slack messages are sent yet. Real webhook goes live alongside Resend go-live in Phase 5.
      </div>
    </div>
  );
}
